'use client';

import { Button, FontIcon, MovieDetails } from '@intigral/shared';
import { MovieDetailsProps } from '@intigral/shared/components/MovieDetails';
import cn from 'classnames';
import React, { forwardRef, isValidElement, ReactElement, ReactNode, useEffect, useState } from 'react';
import styles from './moviepreview.module.sass';

export interface MoviePreviewProps extends React.HTMLAttributes<HTMLDivElement> {
    picture?: string | React.ReactElement;
    headingImage?: string;
    heading?: ReactNode;
    play?: ReactElement;
    reminderButton?: ReactElement;
    watchlistButton?: ReactElement;
    onFavourite?: (next: boolean) => void;
    isFavourite?: boolean;
    endActions?: ReactNode;
    opinion?: 'like' | 'dislike' | null;
    onOpinion?: (next: 'like' | 'dislike' | null) => void;
    onMore?: React.MouseEventHandler<HTMLButtonElement>;
    hideMore?: boolean;
    muted?: boolean;
    onMute?: (next: boolean) => void;
    onFullscreen?: () => void;
    details?: MovieDetailsProps;
}

const glyph = { size: 'smaller', md: 'smaller' } as const;

/** Figma "Frame 10136": shared by MovieCard and VideoPopup */
export const MoviePreview = forwardRef<HTMLDivElement, MoviePreviewProps>(
    (
        {
            picture,
            headingImage,
            heading,
            play,
            reminderButton,
            watchlistButton,
            isFavourite,
            onFavourite,
            endActions,
            opinion,
            onOpinion,
            onMore,
            hideMore = false,
            muted,
            onMute,
            onFullscreen,
            details,
            className,
            ...props
        },
        ref
    ) => {
        const [favourite, setFavourite] = useState(Boolean(isFavourite));
        const [opinionState, setOpinionState] = useState<'like' | 'dislike' | null>(opinion ?? null);
        const [isMuted, setIsMuted] = useState(muted ?? true);

        useEffect(() => setFavourite(Boolean(isFavourite)), [isFavourite]);
        useEffect(() => setOpinionState(opinion ?? null), [opinion]);
        useEffect(() => setIsMuted(muted ?? true), [muted]);

        const handleFavourite = () => {
            const next = !favourite;
            setFavourite(next);
            onFavourite?.(next);
        };

        const handleOpinion = (next: 'like' | 'dislike') => {
            const value = opinionState === next ? null : next;
            setOpinionState(value);
            onOpinion?.(value);
        };

        const handleMute = () => {
            const next = !isMuted;
            setIsMuted(next);
            onMute?.(next);
        };

        return (
            <div ref={ref} className={cn(styles.preview, className)} {...props}>
                <div className={styles.media}>
                    {picture &&
                        (typeof picture === 'string' ? (
                            <img className={styles.still} src={picture} alt="" />
                        ) : (
                            <div className={styles.still}>{picture}</div>
                        ))}
                    <div className={styles.overlay}>
                        {headingImage ? (
                            <img
                                className={styles.logo}
                                src={headingImage}
                                alt={typeof heading === 'string' ? heading : ''}
                            />
                        ) : (
                            heading && <span className={styles.caption}>{heading}</span>
                        )}
                        {/* React still bubbles portal clicks into the card */}
                        {!isValidElement(picture) && (onMute || onFullscreen) && (
                            <div className={styles['media-ctrls']} onClick={(e) => e.stopPropagation()}>
                                {onMute && (
                                    <Button
                                        social
                                        compact
                                        round
                                        className={styles['media-ctrl']}
                                        onClick={handleMute}
                                        aria-pressed={isMuted}
                                        data-test-id="popup-mute"
                                    >
                                        <FontIcon name={isMuted ? 'volume-off' : 'volume-high'} {...glyph} />
                                    </Button>
                                )}
                                {onFullscreen && (
                                    <Button
                                        social
                                        compact
                                        round
                                        className={styles['media-ctrl']}
                                        onClick={onFullscreen}
                                        data-test-id="popup-fullscreen"
                                    >
                                        <FontIcon name="fullscreen" {...glyph} />
                                    </Button>
                                )}
                            </div>
                        )}
                    </div>
                </div>
                <div className={styles.body}>
                    <div className={styles.actions} onClick={(e) => e.stopPropagation()}>
                        <div className={styles.group}>
                            {/* `false` opts out of the default play, as in MovieButtons */}
                            {play}
                            {reminderButton}
                            {play === undefined && reminderButton === undefined && (
                                <Button primary social compact data-test-id="popup-play">
                                    <FontIcon name="play" {...glyph} />
                                </Button>
                            )}
                            {watchlistButton || (
                                <Button
                                    social
                                    compact
                                    onClick={handleFavourite}
                                    aria-pressed={favourite}
                                    data-test-id="popup-favourite"
                                >
                                    {/* keep in sync with AddWatchlistButton */}
                                    <FontIcon name={favourite ? 'close' : 'plus'} {...glyph} />
                                </Button>
                            )}
                            {endActions || (
                                <>
                                    <Button
                                        social
                                        compact
                                        onClick={() => handleOpinion('dislike')}
                                        aria-pressed={opinionState === 'dislike'}
                                        data-test-id="popup-dislike"
                                    >
                                        <FontIcon
                                            name={opinionState === 'dislike' ? 'thumb-down-filled' : 'thumb-down'}
                                            color={opinionState === 'dislike' ? 'orange' : undefined}
                                            {...glyph}
                                        />
                                    </Button>
                                    <Button
                                        social
                                        compact
                                        onClick={() => handleOpinion('like')}
                                        aria-pressed={opinionState === 'like'}
                                        data-test-id="popup-like"
                                    >
                                        <FontIcon
                                            name={opinionState === 'like' ? 'thumb-up-filled' : 'thumb-up'}
                                            color={opinionState === 'like' ? 'orange' : undefined}
                                            {...glyph}
                                        />
                                    </Button>
                                </>
                            )}
                        </div>
                        {!hideMore && (
                            <Button social compact onClick={onMore} data-test-id="popup-more">
                                <FontIcon name="more" {...glyph} />
                            </Button>
                        )}
                    </div>
                    {details && <MovieDetails {...details} className={styles.meta} />}
                </div>
            </div>
        );
    }
);

MoviePreview.displayName = 'MoviePreview';
